import { RegistrationFields } from "@/components/events/RegistrationFields";
import { registrationFormValues } from "@/components/events/eventFormValues";
import { useState } from "react";
import { Info, Clock, Globe, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import { useUpdateParentEvent } from "@/hooks/useParentEvents";
import { toast } from "sonner";
import type { EventResponse } from "@/types";
import { EventImageManager } from "@/components/events/EventImageManager";

function toLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface EditParentEventDialogProps {
  event: EventResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditParentEventDialog({ event, open, onOpenChange }: EditParentEventDialogProps) {
  const updateParent = useUpdateParentEvent();

  function initForm() {
    return {
      ...registrationFormValues(event),
      title: event.title,
      eventDescription: event.eventDescription,
      date: toLocalDatetime(event.date),
      endDate: event.endDate ? toLocalDatetime(event.endDate) : "",
      source: event.source,
      sourceUrl: event.sourceUrl,
      isPreview: event.isPreview,
    };
  }

  const [form, setForm] = useState(initForm);

  const handleOpenChange = (next: boolean) => {
    if (next) setForm(initForm());
    onOpenChange(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateParent.mutateAsync({
        id: event.id,
        registrationOptions: form.registrationOptions,
        registrationNotes: form.registrationNotes,
        title: form.title,
        eventDescription: form.eventDescription,
        date: new Date(form.date).toISOString(),
        endDate: form.endDate ? new Date(form.endDate).toISOString() : undefined,
        source: form.source,
        sourceUrl: form.sourceUrl,
        isPreview: form.isPreview,
      });
      toast.success("Wydarzenie zaktualizowane");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Edytuj cykl wydarzeń"
      subtitle={event.id}
      size="lg"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" form="parent-edit-form" disabled={updateParent.isPending}>
            {updateParent.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
          </Button>
        </>
      }
    >
      <form id="parent-edit-form" onSubmit={handleSubmit} className="space-y-4">
        <FormSection icon={Info} title="Podstawowe informacje">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tytuł</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Opis</Label>
              <Textarea
                value={form.eventDescription}
                onChange={(e) => setForm((f) => ({ ...f, eventDescription: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
        </FormSection>

        <FormSection icon={Clock} title="Termin">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data rozpoczęcia</Label>
              <Input
                type="datetime-local"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Data zakończenia</Label>
              <Input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                required
              />
            </div>
          </div>
        </FormSection>

        <RegistrationFields
          registrationOptions={form.registrationOptions}
          registrationNotes={form.registrationNotes}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        />

        <FormSection icon={Globe} title="Źródło" hint="opcjonalne">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nazwa źródła</Label>
              <Input
                value={form.source}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>URL źródła</Label>
              <Input
                value={form.sourceUrl}
                onChange={(e) => setForm((f) => ({ ...f, sourceUrl: e.target.value }))}
                placeholder="https://..."
              />
            </div>
          </div>
        </FormSection>

        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
          <Checkbox
            id="edit-parent-event-isPreview"
            checked={form.isPreview}
            onCheckedChange={(v) => setForm((f) => ({ ...f, isPreview: !!v }))}
            className="mt-0.5"
          />
          <div className="grid gap-1">
            <Label htmlFor="edit-parent-event-isPreview" className="cursor-pointer font-normal">
              Tylko podgląd admina
            </Label>
            <p className="text-xs text-muted-foreground">
              Widoczne tylko dla adminów. Cykl wydarzeń nie pojawi się na publicznej liście.
            </p>
          </div>
        </div>
      </form>

      <FormSection icon={ImageIcon} title="Zdjęcia">
        <EventImageManager eventId={event.id} eventsBasePath="/admin/events" />
      </FormSection>
    </FormDialog>
  );
}
